import {
  ETAPAS_XABCDE,
  type AchadoXabcde,
  type CenarioExtracao,
  type EtapaXabcde,
  type VeiculoCenarioExtracao,
  type VitimaCenarioExtracao,
} from './cenariosExtracao'
import { PROTOCOLO_NAO_DEFINIDO, protocoloDefinido, type ProtocoloTriagem } from './protocoloTriagem'
import { achadosRevelados, type DeclaracaoClassificacao, type SessaoExtracao } from './sessaoExtracao'

/**
 * Referência PRIVADA de correção de uma vítima — nunca sai na exportação pro
 * aluno, nunca aparece no debriefing antes de `liberarDebriefing`.
 *
 * `classificacaoEsperada` e as demais são texto livre do professor, sempre:
 * não existe cálculo automático de categoria/cor aqui (Fase 5B, decisão
 * registrada em docs/fase-5b-autoria-cenarios.md — ligar uma regra de
 * verdade exigiria confirmar a edição do protocolo e revisão clínica, fora
 * do escopo desta fase).
 */
export interface ReferenciaCorrecaoVitima {
  vitimaId: string
  /** achados que sustentam a classificação — comparados literalmente com o que o aluno encontrou */
  achadosEssenciais: readonly EtapaXabcde[]
  classificacaoEsperada: string
  alternativasAceitas: readonly string[]
  prioridadesAtencao: string
  riscosDeAtraso: string
  restricoesGeometricas: string
  pontosDeJulgamento: string
}

export const referenciaVazia = (vitimaId: string): ReferenciaCorrecaoVitima => ({
  vitimaId,
  achadosEssenciais: [],
  classificacaoEsperada: '',
  alternativasAceitas: [],
  prioridadesAtencao: '',
  riscosDeAtraso: '',
  restricoesGeometricas: '',
  pontosDeJulgamento: '',
})

/**
 * Cenário autoral do professor: o `CenarioExtracao` de sempre — mesmo
 * consumido por `criarSessaoExtracao`, `ocupantesDoCenario`, a cena 3D — mais
 * o que só o professor pode ver: protocolo declarado e referência de
 * correção por vítima.
 */
export interface CenarioAutoral {
  id: string
  cenario: CenarioExtracao
  protocolo: ProtocoloTriagem
  referencia: readonly ReferenciaCorrecaoVitima[]
  criadoEm: string
}

const REGIOES_PADRAO: Record<EtapaXabcde, AchadoXabcde['regiao']> = {
  X: 'corpoTodo',
  A: 'cabeca',
  B: 'torax',
  C: 'corpoTodo',
  D: 'cabeca',
  E: 'corpoTodo',
}

const achadoNaoInformado = (regiao: AchadoXabcde['regiao']): AchadoXabcde => ({
  resumo: 'Não informado',
  detalhe: 'O professor ainda não preencheu este achado.',
  regiao,
  informado: false,
})

const xabcdeNaoInformado = (): Readonly<Record<EtapaXabcde, AchadoXabcde>> =>
  Object.fromEntries(ETAPAS_XABCDE.map((etapa) => [etapa, achadoNaoInformado(REGIOES_PADRAO[etapa])])) as Record<
    EtapaXabcde,
    AchadoXabcde
  >

export function novoVeiculoAutoral(id: string): VeiculoCenarioExtracao {
  return {
    id,
    nome: 'Veículo A',
    configuracao: 'sobreRodas',
    estabilidadeInicial: true,
    riscosIniciais: [],
    acessoInicial: 'livre',
  }
}

export function novaVitimaAutoral(id: string, veiculoId: string): VitimaCenarioExtracao {
  return {
    id,
    veiculoId,
    nome: 'Vítima',
    idAncora: 'motorista',
    idPerfil: 'adultoMasculino',
    estadoClinico: 'estavel',
    perceptivel: [],
    urgencia: 'avaliar',
    encarceramento: 'fisicoI',
    leituraEncarceramento: '',
    aprisionamentos: [],
    xabcde: xabcdeNaoInformado(),
  }
}

/** Cenário autoral vazio: um veículo, uma vítima, nenhum achado preenchido. */
export function novoCenarioAutoral(id: string): CenarioAutoral {
  const veiculoId = `${id}-veiculo-a`
  const vitimaId = `${id}-vitima-a`
  return {
    id,
    cenario: {
      id,
      nome: 'Novo cenário',
      resumo: '',
      objetivo: '',
      veiculos: [novoVeiculoAutoral(veiculoId)],
      vitimas: [novaVitimaAutoral(vitimaId, veiculoId)],
      pausas: [],
    },
    protocolo: PROTOCOLO_NAO_DEFINIDO,
    referencia: [referenciaVazia(vitimaId)],
    criadoEm: new Date().toISOString(),
  }
}

/**
 * Ponto de partida autoral a partir de um cenário já existente (do catálogo
 * fixo ou de outro cenário autoral) — o editor "parte de um dos seis
 * cenários existentes ou de um cenário vazio" (plano, Fase 5B). Os ids
 * internos de vítima/veículo não precisam mudar: só valem dentro do próprio
 * `cenario`, então dois cenários distintos podem reusá-los sem colisão.
 */
export function cenarioAutoralAPartirDe(base: CenarioExtracao, novoId: string): CenarioAutoral {
  return {
    id: novoId,
    cenario: { ...base, id: novoId },
    protocolo: PROTOCOLO_NAO_DEFINIDO,
    referencia: base.vitimas.map((vitima) => referenciaVazia(vitima.id)),
    criadoEm: new Date().toISOString(),
  }
}

export interface AlertaValidacaoCenario {
  nivel: 'erro' | 'aviso'
  mensagem: string
  vitimaId?: string
}

/**
 * Valida coerência do cenário autoral. `erro` impede rodar o cenário numa
 * sessão (referência quebrada, limites de vítimas/veículos); `aviso` é só
 * informativo — achado não preenchido, referência de correção incompleta —
 * porque não há correção automática que precise bloquear a publicação por
 * causa disso (decisão da Fase 5B).
 */
export function validarCenarioAutoral(autoral: CenarioAutoral): readonly AlertaValidacaoCenario[] {
  const alertas: AlertaValidacaoCenario[] = []
  const { cenario, protocolo, referencia } = autoral

  if (!cenario.nome.trim()) alertas.push({ nivel: 'erro', mensagem: 'O cenário precisa de um nome.' })
  if (cenario.veiculos.length === 0) alertas.push({ nivel: 'erro', mensagem: 'O cenário precisa de pelo menos um veículo.' })
  if (cenario.veiculos.length > 2) alertas.push({ nivel: 'erro', mensagem: 'No máximo dois veículos por cenário.' })
  if (cenario.vitimas.length === 0) alertas.push({ nivel: 'erro', mensagem: 'O cenário precisa de pelo menos uma vítima.' })
  if (cenario.vitimas.length > 2) alertas.push({ nivel: 'erro', mensagem: 'No máximo duas vítimas por cenário.' })

  const idsVeiculo = new Set(cenario.veiculos.map((v) => v.id))
  if (idsVeiculo.size !== cenario.veiculos.length) alertas.push({ nivel: 'erro', mensagem: 'Dois veículos com o mesmo id.' })
  const idsVitima = new Set(cenario.vitimas.map((v) => v.id))
  if (idsVitima.size !== cenario.vitimas.length) alertas.push({ nivel: 'erro', mensagem: 'Duas vítimas com o mesmo id.' })

  for (const vitima of cenario.vitimas) {
    const rotulo = vitima.nome || vitima.id
    if (!vitima.nome.trim()) alertas.push({ nivel: 'erro', mensagem: 'Vítima sem nome.', vitimaId: vitima.id })
    if (!idsVeiculo.has(vitima.veiculoId)) {
      alertas.push({ nivel: 'erro', mensagem: `"${rotulo}" referencia um veículo que não existe.`, vitimaId: vitima.id })
    }

    const incompletos = ETAPAS_XABCDE.filter((etapa) => vitima.xabcde[etapa]?.informado === false)
    if (incompletos.length > 0) {
      alertas.push({
        nivel: 'aviso',
        mensagem: `"${rotulo}": achado(s) ${incompletos.join(', ')} ainda não foram preenchidos.`,
        vitimaId: vitima.id,
      })
    }

    const ref = referencia.find((r) => r.vitimaId === vitima.id)
    if (!ref) {
      alertas.push({ nivel: 'aviso', mensagem: `"${rotulo}" não tem referência de correção.`, vitimaId: vitima.id })
    } else if (!ref.classificacaoEsperada.trim()) {
      alertas.push({
        nivel: 'aviso',
        mensagem: `"${rotulo}": a classificação esperada da referência está vazia.`,
        vitimaId: vitima.id,
      })
    }
  }

  if (!protocoloDefinido(protocolo)) {
    alertas.push({ nivel: 'aviso', mensagem: 'Nenhum protocolo de triagem foi declarado (nome e fonte).' })
  }

  return alertas
}

/** Estrutura íntegra o bastante pra rodar numa sessão: sem nenhum erro (avisos não bloqueiam). */
export const cenarioAutoralValido = (autoral: CenarioAutoral): boolean =>
  validarCenarioAutoral(autoral).every((alerta) => alerta.nivel !== 'erro')

/** Sem nenhum alerta, erro ou aviso — pronto para uso em aula sem pendências visíveis ao professor. */
export const cenarioProntoParaAlunos = (autoral: CenarioAutoral): boolean =>
  validarCenarioAutoral(autoral).length === 0

/**
 * O que o aluno pode ver: o `CenarioExtracao` já é a forma seguindo — sem
 * `protocolo`/`referencia`. Mesma função usada por "pré-visualizar como
 * aluno" e pela exportação da versão do aluno.
 */
export const cenarioParaAluno = (autoral: CenarioAutoral): CenarioExtracao => autoral.cenario

export const referenciaDaVitima = (
  autoral: CenarioAutoral,
  vitimaId: string,
): ReferenciaCorrecaoVitima | undefined => autoral.referencia.find((item) => item.vitimaId === vitimaId)

export interface ComparacaoReferenciaVitima {
  vitimaId: string
  achadosEncontrados: readonly EtapaXabcde[]
  achadosEssenciaisFaltando: readonly EtapaXabcde[]
  classificacaoAluno: DeclaracaoClassificacao | null
  referencia: ReferenciaCorrecaoVitima
}

/**
 * Compara o que o aluno encontrou/declarou com a referência do professor —
 * SÓ comparação literal de conjuntos (quais letras do XABCDE o aluno
 * revelou vs. quais a referência marca como essenciais), nunca um veredito.
 * O debriefing mostra lado a lado; quem julga certo/errado é o professor.
 */
export function compararComReferencia(
  sessao: SessaoExtracao,
  referencia: readonly ReferenciaCorrecaoVitima[],
): readonly ComparacaoReferenciaVitima[] {
  return referencia.map((ref) => {
    const encontrados = achadosRevelados(sessao, ref.vitimaId).map((achado) => achado.etapa)
    return {
      vitimaId: ref.vitimaId,
      achadosEncontrados: encontrados,
      achadosEssenciaisFaltando: ref.achadosEssenciais.filter((etapa) => !encontrados.includes(etapa)),
      classificacaoAluno: sessao.classificacoesAluno[ref.vitimaId] ?? null,
      referencia: ref,
    }
  })
}
